import type { DragEndEvent } from '@dnd-kit/core';
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { Box, Paper, Stack, Typography } from '@mui/material';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { List, useListRef } from 'react-window';

import { DragOverlayContent } from './components/DragOverlayContent';
import { EditorActions } from './components/EditorActions';
import { HistoryButton } from './components/HistoryButton';
import type { MergeMenuState } from './components/MergeMenu';
import { MergeMenu } from './components/MergeMenu';
import { PageGroup } from './components/PageGroup';
import { RecycleBin } from './components/RecycleBin';
import { SummaryStats } from './components/SummaryStats';
import {
  mergeWithAdjacent,
  reorganizeGroups,
  splitPageFromGroups,
} from './utils/merge';

const messages = defineMessages({
  title: {
    id: 'discernSplitEditor.title',
    defaultMessage: 'PDF Split Editor',
  },
});

interface PdfSplitEditorProps {
  totalPages: number;
  currentPage: number;
  removedPages: number[];
  externalPageGroups?: number[][];
  isProcessing?: boolean;
  isSaving?: boolean;
  discernDocumentId?: string;
  onRemovePage: (pageNum: number) => void;
  onSaveSplits?: (splits: number[], pageGroups: number[][]) => void;
  onStartAutoCuration?: (splits: number[], pageGroups: number[][]) => void;
  onPageClick: (page: number) => void;
  onRestorePage: (pagesToRestore?: number[]) => void;
  onGroupsChange?: (pageGroups: number[][]) => void;
  onReorderPages?: (newGroups: number[][]) => void;
}

interface RowProps {
  expandedAccordion: number | false;
  pageGroups: number[][];
}

function rowHeight(index: number, props: RowProps) {
  if (props.expandedAccordion === index) {
    const numPages = props.pageGroups[index].length;
    const baseHeight = 100;
    const groupHeight = 47;
    return baseHeight + groupHeight * numPages + numPages;
  }
  return 54;
}

const PdfSplitEditor: React.FC<PdfSplitEditorProps> = ({
  totalPages,
  currentPage,
  onSaveSplits,
  onStartAutoCuration,
  onPageClick,
  onRemovePage,
  onRestorePage,
  removedPages,
  onGroupsChange,
  onReorderPages,
  externalPageGroups,
  isProcessing = false,
  isSaving = false,
}) => {
  const { formatMessage } = useIntl();
  const listRef = useListRef(null);
  // State for page groups - each page starts in its own group
  const [pageGroups, setPageGroups] = useState<number[][]>([]);
  const [expandedAccordion, setExpandedAccordion] = useState<number | false>(
    false
  );
  const isInternalUpdate = useRef(false);

  const pageGroupsKey = JSON.stringify(pageGroups); // to calculate rerenders

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 6, // prevents accidental drags
      },
    })
  );

  // Update groups from external source (e.g., drag and drop from grid view or saved state)
  useEffect(() => {
    if (externalPageGroups && externalPageGroups.length > 0) {
      const currentGroupsStr = JSON.stringify(pageGroups);
      const externalGroupsStr = JSON.stringify(externalPageGroups);
      const groupsChanged = currentGroupsStr !== externalGroupsStr;

      if (groupsChanged) {
        isInternalUpdate.current = false;
        setPageGroups(externalPageGroups);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalPageGroups, totalPages]); // Added totalPages to dependency

  // Refs for the split editor container and groups container
  const splitEditorRef = useRef<HTMLDivElement>(null);

  const [menuState, setMenuState] = useState<MergeMenuState | null>(null);

  const handleOpenMergeMenu = (groupIndex: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setMenuState({
      mouseX: e.clientX,
      mouseY: e.clientY,
      groupIndex,
    });
  };

  const handleCloseMergeMenu = () => {
    setMenuState(null);
  };

  const handleMergePages = (newGroups: number[][]) => {
    isInternalUpdate.current = true;
    setPageGroups(newGroups);
    setMenuState(null);
  };

  useEffect(() => {
    // Only initialize to individual pages if:
    // 1. We have totalPages
    // 2. pageGroups is empty
    // 3. externalPageGroups is explicitly an empty array (not undefined, which means still loading)
    if (
      totalPages > 0 &&
      pageGroups.length === 0 &&
      Array.isArray(externalPageGroups) &&
      externalPageGroups.length === 0
    ) {
      isInternalUpdate.current = true;
      const allPages = Array.from({ length: totalPages }, (_, i) => [i + 1]);
      const remainingPages = allPages.filter(
        ([page]) => !removedPages.includes(page)
      );
      setPageGroups(remainingPages);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalPages, pageGroupsKey, externalPageGroups]);

  useEffect(() => {
    if (pageGroups.length > 0 && totalPages > 0) {
      const pagesInGroups = new Set(pageGroups.flat());

      const needsFiltering = pageGroups.some((group) =>
        group.some((page) => removedPages.includes(page))
      );

      const allPages = Array.from({ length: totalPages }, (_, i) => i + 1);
      const pagesToRestore = allPages.filter(
        (page) => !pagesInGroups.has(page) && !removedPages.includes(page)
      );

      if (needsFiltering || pagesToRestore.length > 0) {
        isInternalUpdate.current = true;
        setPageGroups((prev) => {
          let updated = prev
            .map((group) =>
              group.filter((page) => !removedPages.includes(page))
            )
            .filter((group) => group.length > 0);

          if (pagesToRestore.length > 0) {
            const restoredGroups = pagesToRestore.map((page) => [page]);
            updated = [...updated, ...restoredGroups];

            updated = reorganizeGroups(updated);
          }

          return updated;
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [removedPages, totalPages]);

  useEffect(() => {
    if (onGroupsChange && isInternalUpdate.current && pageGroups.length > 0) {
      onGroupsChange(pageGroups);
    }
  }, [pageGroups, onGroupsChange]);

  const splits = useMemo(() => {
    const splitPoints: number[] = [];
    let pageCounter = 0;

    pageGroups.forEach((group, index) => {
      if (index > 0) {
        splitPoints.push(pageCounter);
      }
      pageCounter += group.length;
    });

    return splitPoints;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageGroupsKey]);

  const handleSplitPage = (pageNum: number) => {
    isInternalUpdate.current = true;
    setPageGroups((prev) => splitPageFromGroups(prev, pageNum));
  };

  const handleMergeWithAdjacent = (
    groupIndex: number,
    direction: 'prev' | 'next'
  ) => {
    isInternalUpdate.current = true;
    setPageGroups((prev) => mergeWithAdjacent(prev, groupIndex, direction));
  };

  const handleSplitAll = () => {
    isInternalUpdate.current = true;
    setPageGroups(Array.from({ length: totalPages }, (_, i) => [i + 1]));
  };

  const handleMergeAll = () => {
    isInternalUpdate.current = true;
    setPageGroups([Array.from({ length: totalPages }, (_, i) => i + 1)]);
  };

  const handleSaveSplits = () => {
    if (onSaveSplits) {
      onSaveSplits(splits, pageGroups);
    }
  };

  const handleStartAutoCuration = () => {
    // Trigger auto curation process via Discern API
    if (onStartAutoCuration) {
      onStartAutoCuration(splits, pageGroups);
    }
  };

  const handleGroupChange = (groupIndex: number, changes: number[]) => {
    const newPageGroups = [...pageGroups];
    newPageGroups[groupIndex] = changes;

    setPageGroups(newPageGroups);

    if (onGroupsChange) {
      isInternalUpdate.current = true;
      onGroupsChange(newPageGroups);
    }

    if (onReorderPages) {
      onReorderPages(newPageGroups);
    }
  };

  const handleRemovePage = (pageNum: number) => {
    onRemovePage(pageNum);
    if (currentPage === pageNum) {
      onPageClick(pageNum + 1);
    }
  };

  const handleRestoreLastPage = () => {
    if (removedPages.length === 0) return;
    onRestorePage();
  };

  const handleDragEnd = (e: DragEndEvent) => {
    const draggedPage = e.active.id as number;
    const targetPage = e.over?.id as number | undefined;
    const groupIndex = pageGroups.findIndex((g) => g.includes(draggedPage));
    if (groupIndex === -1) {
      return;
    }
    const group = pageGroups[groupIndex];

    const sourcePageIndex = group.indexOf(draggedPage);
    const targetPageIndex = group.indexOf(targetPage ?? -1);

    if (sourcePageIndex === -1 || targetPageIndex === -1) return;

    const newGroup = [...group];
    const [movedPage] = newGroup.splice(sourcePageIndex, 1);
    newGroup.splice(targetPageIndex, 0, movedPage);

    handleGroupChange(groupIndex, newGroup);
  };

  // Auto-scroll groups container when current page changes
  useEffect(() => {
    if (!listRef.current) return;
    const index = pageGroups.findIndex((p) => p.includes(currentPage - 1)); // page starts at 1
    if (index === -1) {
      return;
    }
    listRef.current.scrollToRow({
      index: index,
      behavior: 'auto',
      align: 'center',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, listRef, pageGroupsKey]);

  // Auto-expand accordion when current page changes (e.g., from grid view click)
  useEffect(() => {
    const groupIndex = pageGroups.findIndex((group) =>
      group.includes(currentPage)
    );

    if (groupIndex !== -1) {
      setExpandedAccordion(groupIndex);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageGroupsKey]);

  const groupNumbers = React.useMemo(() => {
    let groupCounter = 0;
    const groupNumbers = new Map<number, number>();

    pageGroups.forEach((group, idx) => {
      if (group.length > 1) {
        groupCounter++;
        groupNumbers.set(idx, groupCounter);
      }
    });
    return groupNumbers;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageGroupsKey]);

  return (
    <Stack
      sx={{
        width: '320px',
        height: '100%',
      }}
    >
      <Stack direction="row" justifyContent="space-between">
        <Typography variant="h6" sx={{ mb: 2 }}>
          {formatMessage(messages.title)}
        </Typography>
        <Stack direction="row">
          <HistoryButton
            onRestorePage={handleRestoreLastPage}
            disabled={!removedPages.length}
          />
          <RecycleBin
            removedPages={removedPages}
            onRestorePage={onRestorePage}
          />
        </Stack>
      </Stack>

      <Paper
        ref={splitEditorRef}
        elevation={2}
        sx={{ flex: 1, overflow: 'auto', p: 2 }}
      >
        <SummaryStats pageGroups={pageGroups} />

        {/* Scrollable groups container */}
        <Box
          sx={{
            height: '400px',
            overflowY: 'auto',
            overflowX: 'hidden',
            mb: 3,
            pr: 1,
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              bgcolor: 'grey.100',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              bgcolor: 'grey.400',
              borderRadius: '4px',
              '&:hover': {
                bgcolor: 'grey.600',
              },
            },
            '& > .react-pdf-document': {
              height: '100%',
            },
          }}
        >
          <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
            <DragOverlayContent />
            <List
              className="react-pdf-document"
              rowCount={pageGroups.length}
              rowHeight={rowHeight}
              rowProps={{ expandedAccordion, pageGroups }}
              rowComponent={({ index: groupIndex, style }) => {
                const group = pageGroups[groupIndex];
                const firstPage = group[0];
                const canSplitPrev = groupIndex > 0;
                const canSplitNext = groupIndex < pageGroups.length - 1;
                const nextGroup = pageGroups[groupIndex + 1]?.[0];
                const prevGroup = pageGroups[groupIndex - 1]?.[0];
                const selected = group.includes(currentPage);
                const multiPageGroupNumber = groupNumbers.get(groupIndex) || 1;
                const groupTitle =
                  group.length === 1
                    ? `Page ${firstPage}`
                    : `Group ${multiPageGroupNumber}`;

                return (
                  <div style={style}>
                    <PageGroup
                      canSplitNext={canSplitNext}
                      canSplitPrev={canSplitPrev}
                      nextGroup={nextGroup}
                      prevGroup={prevGroup}
                      group={group}
                      groupIndex={groupIndex}
                      isExpanded={expandedAccordion === groupIndex}
                      groupTitle={groupTitle}
                      selected={selected}
                      currentPage={currentPage}
                      onExpand={setExpandedAccordion}
                      onRemovePage={handleRemovePage}
                      onMergeWithAdjacent={handleMergeWithAdjacent}
                      onPageClick={onPageClick}
                      onSplitPage={handleSplitPage}
                      onOpenMergeMenu={handleOpenMergeMenu}
                    />
                  </div>
                );
              }}
              listRef={listRef}
            />
          </DndContext>
        </Box>
        {/* Action Buttons */}
        <EditorActions
          isProcessing={isProcessing}
          isSaving={isSaving}
          onMergeAll={handleMergeAll}
          onSaveSplits={handleSaveSplits}
          onSplitAll={handleSplitAll}
          onStartAutoCuration={handleStartAutoCuration}
        />
      </Paper>
      <MergeMenu
        onClose={handleCloseMergeMenu}
        onMergePages={handleMergePages}
        pageGroups={pageGroups}
        menuState={menuState}
      />
    </Stack>
  );
};

export default PdfSplitEditor;
